import React from "react";

// Source
import StoreRoute from "../../store/Route";
import StoreRole from "../../store/Role";
import StorePreference from "../../store/Preference";
import * as HelperSrc from "../../HelperSrc";
import * as ModelInvoice from "../../model/custom/Invoice";
import * as ModelRoute from "../../model/Route";
import * as ModelCustomRoute from "../../model/custom/Route";
import * as ModelRole from "../../model/Role";
import * as ModelHelperSrc from "../../model/HelperSrc";
import * as ApiInvoice from "../../api/custom/Invoice";
import * as ModelPagination from "../../model/Pagination";
import * as ModelPreference from "../../model/Preference";
import ControllerAlert from "../Alert";

export default class ControllerInvoiceUploadList {
    // Variable
    private storeRoute: ModelRoute.Istore<ModelCustomRoute.Idata>;
    private storageRouteData = React.useRef<ModelCustomRoute.Idata | undefined>(undefined);
    private storeRole: ModelRole.Istore;
    private storageRoleData = React.useRef<ModelRole.Idata | undefined>(undefined);
    private storePreference: ModelPreference.Istore<ModelPreference.Idata>;
    private storagePreferenceData = React.useRef<ModelPreference.Idata | undefined>(undefined);

    private controllerAlert: ControllerAlert;

    private elementFileName = React.useRef<HTMLInputElement | null>(null);
    private elementClientId = React.useRef<HTMLInputElement | null>(null);
    private elementLatestUpdate = React.useRef<HTMLInputElement | null>(null);
    private elementLatestUpdateById = React.useRef<HTMLInputElement | null>(null);
    private elementViewMain = React.useRef<HTMLElement | null>(null);

    private isRendered = React.useRef<boolean>(false);
    private idRemove = React.useRef(0);
    private filter = React.useRef<ModelPagination.CustomPagination.IfilterInvoice | undefined>(undefined);
    private dataMainList = React.useRef<ModelInvoice.Idata[] | undefined>(undefined);
    private language = React.useRef<string>("");

    getStorageRole: ModelRole.Idata | undefined;

    getApiLoadingList: Record<string, boolean | undefined>;
    setApiLoadingList: React.Dispatch<React.SetStateAction<Record<string, boolean | undefined>>>;

    getSelectOption: ModelInvoice.IselectOption | undefined;
    setSelectOption: React.Dispatch<React.SetStateAction<ModelInvoice.IselectOption | undefined>>;

    getIsDialogOpen: boolean;
    setIsDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;

    getPagination: ModelPagination.Idata | undefined;
    setPagination: React.Dispatch<React.SetStateAction<ModelPagination.Idata | undefined>>;

    getFilter: ModelInvoice.Idata | undefined;
    setFilter: React.Dispatch<React.SetStateAction<ModelInvoice.Idata | undefined>>;

    getDataMainList: ModelInvoice.Idata[] | undefined;
    setDataMainList: React.Dispatch<React.SetStateAction<ModelInvoice.Idata[] | undefined>>;

    getUrlFile: string | undefined;
    setUrlFile: React.Dispatch<React.SetStateAction<string | undefined>>;

    getInvoicePreparerList: Record<number, string[]>;
    setInvoicePreparerList: React.Dispatch<React.SetStateAction<Record<number, string[]>>>;

    getLanguage: string;
    setLanguage: React.Dispatch<React.SetStateAction<string>>;

    getElementClientIdValue: string;
    setElementClientIdValue: React.Dispatch<React.SetStateAction<string>>;

    getElementLatestUpdateByIdValue: string;
    setElementLatestUpdateByIdValue: React.Dispatch<React.SetStateAction<string>>;

    // Method
    constructor(isRendered: boolean) {
        this.storeRoute = new StoreRoute();
        this.storageRouteData.current = this.storeRoute.storageGet();
        this.storeRole = new StoreRole();
        this.storageRoleData.current = this.storeRole.storageGet();
        this.storePreference = new StorePreference();
        this.storagePreferenceData.current = this.storePreference.storageGet();

        this.controllerAlert = new ControllerAlert(isRendered);

        this.isRendered.current = isRendered;

        const [getStorageRole] = React.useState<ModelRole.Idata | undefined>(this.storageRoleData.current);
        this.getStorageRole = getStorageRole;

        const [getApiLoadingList, setApiLoadingList] = React.useState<Record<string, boolean | undefined>>({});
        this.getApiLoadingList = getApiLoadingList;
        this.setApiLoadingList = setApiLoadingList;

        const [getSelectOption, setSelectOption] = React.useState<ModelInvoice.IselectOption | undefined>(undefined);
        this.getSelectOption = getSelectOption;
        this.setSelectOption = setSelectOption;

        const [getIsDialogOpen, setIsDialogOpen] = React.useState(false);
        this.getIsDialogOpen = getIsDialogOpen;
        this.setIsDialogOpen = setIsDialogOpen;

        const [getPagination, setPagination] = React.useState<ModelPagination.Idata | undefined>(undefined);
        this.getPagination = getPagination;
        this.setPagination = setPagination;

        const [getFilter, setFilter] = React.useState<ModelInvoice.Idata | undefined>(undefined);
        this.getFilter = getFilter;
        this.setFilter = setFilter;

        const [getDataMainList, setDataMainList] = React.useState<ModelInvoice.Idata[] | undefined>(this.dataMainList.current);
        this.getDataMainList = getDataMainList;
        this.setDataMainList = setDataMainList;

        const [getUrlFile, setUrlFile] = React.useState<string | undefined>(undefined);
        this.getUrlFile = getUrlFile;
        this.setUrlFile = setUrlFile;

        const [getInvoicePreparerList, setInvoicePreparerList] = React.useState<Record<number, string[]>>({});
        this.getInvoicePreparerList = getInvoicePreparerList;
        this.setInvoicePreparerList = setInvoicePreparerList;

        const [getLanguage, setLanguage] = React.useState<string>(this.language.current);
        this.getLanguage = getLanguage;
        this.setLanguage = setLanguage;

        const [getElementClientIdValue, setElementClientIdValue] = React.useState<string>("");
        this.getElementClientIdValue = getElementClientIdValue;
        this.setElementClientIdValue = setElementClientIdValue;

        const [getElementLatestUpdateByIdValue, setElementLatestUpdateByIdValue] = React.useState<string>("");
        this.getElementLatestUpdateByIdValue = getElementLatestUpdateByIdValue;
        this.setElementLatestUpdateByIdValue = setElementLatestUpdateByIdValue;
    }

    initializeHook = (): void => {
        React.useEffect(() => {
            HelperSrc.domObserver(".view_invoice_upload_list", () => {
                this.initializeHtmlElement();
            });

            this.apiSelectOption("upload");

            this.apiUploadList();
        }, []);

        React.useEffect(() => {
            this.storageRouteData.current = this.storeRoute.storageGet();
            this.storageRoleData.current = this.storeRole.storageGet();
        }, [this.storageRouteData.current, this.storageRoleData.current]);

        React.useEffect(() => {
            this.storagePreferenceData.current = this.storePreference.storageGet();

            if (this.storagePreferenceData.current) {
                const initialLanguage = this.language.current;

                if (this.language.current !== this.storagePreferenceData.current.language) {
                    this.language.current = this.storagePreferenceData.current.language;
                    this.setLanguage(this.language.current);

                    if (initialLanguage !== "") {
                        this.apiSelectOption("upload");
                    }
                }
            }
        }, [this.storagePreferenceData.current]);
    };

    apiUploadList = (isFilter = false): void => {
        this.filterParameter(isFilter);

        if (this.filter.current) {
            this.setApiLoadingList((prev) => ({
                ...prev,
                uploadList: true
            }));

            ApiInvoice.uploadList(this.filter.current, this.filter.current.sort_order, this.filter.current.page, (response) => {
                this.setApiLoadingList((prev) => ({
                    ...prev,
                    uploadList: false
                }));

                if (response instanceof Error || response.message.error) {
                    if (response instanceof Error) {
                        HelperSrc.writeLog("error", response.message);
                    } else {
                        this.controllerAlert.show("error", response.message.error, false);
                    }

                    return;
                }

                this.setPagination(response.pagination);

                this.dataMainList.current = response.pagination.data as unknown as ModelInvoice.Idata[];
                this.setDataMainList(this.dataMainList.current);

                for (const data of this.dataMainList.current) {
                    ApiInvoice.invoicePreparer(data.id, (response) => {
                        if (response instanceof Error || response.message.error) {
                            if (response instanceof Error) {
                                HelperSrc.writeLog("error", response.message);
                            } else {
                                this.controllerAlert.show("error", response.message.error, false);
                            }

                            return;
                        }

                        this.setInvoicePreparerList((prev) => ({
                            ...prev,
                            [data.id]: response.data.result as unknown as string[]
                        }));
                    });
                }

                this.controllerAlert.show("success", response.message.success);
            });
        }
    };

    clickFilterApply = (): void => {
        this.apiUploadList(true);
    };

    clickCreate = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>, data: ModelInvoice.Idata): void => {
        const element = event.target as HTMLElement;
        const tagName = element.tagName;

        if (this.storageRouteData.current && tagName !== "path") {
            HelperSrc.routerHistoryPush(this.storageRouteData.current.invoice_profile.path, data as unknown as Record<string, unknown>);
        }
    };

    clickRemove = (event: React.MouseEvent<SVGSVGElement, MouseEvent>, id: number): void => {
        event.stopPropagation();

        this.idRemove.current = id;

        this.setIsDialogOpen(true);
    };

    clickDialogRemoveConfirm = (): void => {
        if (this.idRemove.current > 0) {
            this.setApiLoadingList((prev) => ({
                ...prev,
                removeData: true
            }));

            ApiInvoice.removeData(this.idRemove.current, (response) => {
                this.setApiLoadingList((prev) => ({
                    ...prev,
                    removeData: false
                }));

                this.setIsDialogOpen(false);

                if (response instanceof Error || response.message.error) {
                    if (response instanceof Error) {
                        HelperSrc.writeLog("error", response.message);
                    } else {
                        this.controllerAlert.show("error", response.message.error, false);
                    }

                    return;
                }

                this.controllerAlert.show("success", response.message.success);

                this.apiUploadList();
            });
        }
    };

    clickDialogRemoveCancel = (): void => {
        this.setIsDialogOpen(false);
    };

    clickShow = (event: React.MouseEvent<SVGSVGElement, MouseEvent>, id: number): void => {
        this.setApiLoadingList((prev) => ({
            ...prev,
            previewFile: true
        }));

        event.stopPropagation();

        const formData = new FormData();
        formData.append("id", id as unknown as string);

        ApiInvoice.previewFile(formData, (response) => {
            this.setApiLoadingList((prev) => ({
                ...prev,
                previewFile: false
            }));

            if (response instanceof Error || response.message.error) {
                if (response instanceof Error) {
                    HelperSrc.writeLog("error", response.message);
                } else {
                    this.controllerAlert.show("error", response.message.error, false);
                }

                return;
            }

            const decodedData = window.atob(response.data.result as unknown as string);
            const uint8Array = new Uint8Array(decodedData.length);

            for (let a = 0; a < decodedData.length; a++) {
                uint8Array[a] = decodedData.charCodeAt(a);
            }

            const blob = new Blob([uint8Array], { type: "application/pdf" });
            const url = URL.createObjectURL(blob);

            this.setUrlFile(url);
        });
    };

    clickClose = (): void => {
        this.setUrlFile("");

        this.setApiLoadingList((prev) => ({
            ...prev,
            previewFile: undefined
        }));
    };

    private initializeHtmlElement = (): void => {
        if (!this.elementViewMain.current) {
            this.elementFileName.current = document.querySelector<HTMLInputElement>("[name='file_name']");
            this.elementClientId.current = document.querySelector<HTMLInputElement>("[name='client_id']");
            this.elementLatestUpdate.current = document.querySelector<HTMLInputElement>("[name='latest_update']");
            this.elementLatestUpdateById.current = document.querySelector<HTMLInputElement>("[name='latest_update_by_id']");
            this.elementViewMain.current = document.querySelector<HTMLElement>(".view_invoice_upload_list");
        }
    };

    private apiSelectOption = (mode: string): void => {
        this.setApiLoadingList((prev) => ({
            ...prev,
            selectOption: true
        }));

        ApiInvoice.selectOption(mode, (response) => {
            this.setApiLoadingList((prev) => ({
                ...prev,
                selectOption: false
            }));

            if (response instanceof Error || response.message.error) {
                if (response instanceof Error) {
                    HelperSrc.writeLog("error", response.message);
                } else {
                    this.controllerAlert.show("error", response.message.error, false);
                }

                return;
            }

            this.setSelectOption(response.data.result as unknown as ModelInvoice.IselectOption);
        });
    };

    private filterParameter = (isFilter: boolean): void => {
        const pageUrl = HelperSrc.urlVariable("page") && !isFilter ? HelperSrc.urlVariable("page") : "1";
        const sortOrderUlr = HelperSrc.urlVariable("sort_order") && !isFilter ? HelperSrc.urlVariable("sort_order") : "desc";
        const fileNameUrl = HelperSrc.urlVariable("file_name") && !isFilter ? HelperSrc.urlVariable("file_name") : "";
        const clientIdUrl = HelperSrc.urlVariable("client_id") && !isFilter ? HelperSrc.urlVariable("client_id") : "";
        const latestUpdateUrl = HelperSrc.urlVariable("latest_update") && !isFilter ? HelperSrc.urlVariable("latest_update") : "";
        const latestUpdateByIdUrl = HelperSrc.urlVariable("latest_update_by_id") && !isFilter ? HelperSrc.urlVariable("latest_update_by_id") : "";

        const fileNameResult =
            this.elementFileName.current && this.elementFileName.current.value !== fileNameUrl ? this.elementFileName.current.value : fileNameUrl;
        const clientIdResult =
            this.elementClientId.current && this.elementClientId.current.value !== clientIdUrl ? this.elementClientId.current.value : clientIdUrl;
        const latestUpdateResult =
            this.elementLatestUpdate.current && this.elementLatestUpdate.current.value !== latestUpdateUrl
                ? this.elementLatestUpdate.current.value
                : latestUpdateUrl;
        const latestUpdateByIdResult =
            this.elementLatestUpdateById.current && this.elementLatestUpdateById.current.value !== latestUpdateByIdUrl
                ? this.elementLatestUpdateById.current.value
                : latestUpdateByIdUrl;

        this.filter.current = {
            page: pageUrl,
            sort_order: sortOrderUlr,
            file_name: fileNameResult as string & ModelHelperSrc.IvalidationForm,
            client_id: clientIdResult as unknown as number & ModelHelperSrc.IvalidationForm,
            latest_update: latestUpdateResult as string & ModelHelperSrc.IvalidationForm,
            latest_update_by_id: latestUpdateByIdResult as unknown as number & ModelHelperSrc.IvalidationForm
        } as unknown as ModelPagination.CustomPagination.IfilterInvoice;
        this.setFilter(this.filter.current);

        if (this.storageRouteData.current) {
            HelperSrc.routerHistoryPush(
                `${this.storageRouteData.current.invoice_upload_list.path}?page=${this.filter.current.page}&sort_order=${this.filter.current.sort_order}&` +
                    `file_name=${this.filter.current.file_name}&` +
                    `client_id=${this.filter.current.client_id}&` +
                    `latest_update=${this.filter.current.latest_update}&` +
                    `latest_update_by_id=${this.filter.current.latest_update_by_id}`,
                {},
                "",
                true
            );
        }
    };
}
